/**
 * Touch wording of the game's help (port addition): the original was played with a keyboard and a mouse, and its
 * tutorial, weapon descriptions, tasks and hints name keys ("Space", "the Up and Down arrows", "Q", "1-5", "Escape")
 * and mouse gestures ("click", "double-click", "hover the cursor"). On a touch screen (the client's touch mode:
 * phones, the Android app, a touch laptop after its first real touch) the same places describe the on-screen controls
 * of src/client/platform/touch-controls.ts instead: ◄ ► walk, ▲ ▼ aim, the jump and back-flip buttons, the fire button
 * (held to charge, released to shoot), the fuse and bounce buttons, the backpack (weapon sheet) button, a tap on the map
 * plus the fire button for a target, ✕ to cancel it, a finger held on an item for its hint.
 *
 * One mechanism for every such text (src/game/runtime/i18n.ts translate): in touch mode the text the active language
 * shows is rewritten by that language's rules below, after the catalogue translated it - a rule replaces the desktop
 * wording (`from`, exactly as that language shows it: the Russian source for 'ru', the catalogue's translation for
 * 'uk' / 'en') with the touch wording (`to`). A rule is a fragment, so it also applies inside the texts the game
 * composes (a weapon's description after its stats). Desktop keeps the original texts untouched.
 *
 * The texts of the SWF screens are HTML without word wrap: `<sbr />` breaks the lines, so a touch text keeps the
 * markup of the text it replaces and breaks its own lines to fit the same field (tests/assets/touch-texts.test.ts
 * measures them). A no-break space keeps a group of buttons (▲ ▼) on one line where the game wraps the text. This
 * file holds text that is already in its language: tools/i18n/extract.mjs skips it.
 */
import type { Language } from './i18n';

/** Desktop wording (a fragment of the shown text, or a pattern) -> touch wording, in one language. */
export interface TouchRule {
  from: string | RegExp;
  to: string;
}

/** A paragraph of the SWF help texts (Arial 11, black, left), as the asset texts write it. */
const P = (inner: string, size = 11): string =>
  `<p align="left"><font face="Arial" size="${size}" color="#000000" letterSpacing="0.000000" kerning="0">${inner}</font></p>`;
/** A control and what it does, as the tutorial's own key lines: the control in bold. */
const K = (control: string, what: string): string => P(`<b>${control}</b><font face="Arial">${what}</font>`);
const EMPTY = '<p align="left"></p>';

/** Tutorial, last page ("Базовое управление"): everything after the introduction, from its first key line to the end. */
const TUTORIAL_KEYS = {
  ru: /<p align="left"><font [^>]*><b>Стрелки Влево\/Вправо \/ A \/ D<\/b>[\s\S]*$/,
  uk: /<p align="left"><font [^>]*><b>Стрілки Вліво\/Вправо \/ A \/ D<\/b>[\s\S]*$/,
  en: /<p align="left"><font [^>]*><b>Left\/Right arrows \/ A \/ D<\/b>[\s\S]*$/,
} as const;

const TUTORIAL_TOUCH = {
  ru: [
    K('◄\u00A0►', ' - идти червем влево и вправо;'),
    K('▲\u00A0▼', ' - смена прицела (у оружия, которое целится);'),
    K('Прыжок и сальто', ' - прыжок вперед и сальто назад;'),
    K('Кнопка огня', ' - стрелять! Удерживайте ее, чтобы набрать силу <sbr />выстрела, и отпустите, чтобы выстрелить.'),
    EMPTY,
    P('Если у оружия нет регулировки силы выстрела, достаточно <sbr />одного нажатия. Для выбора оружия нажмите кнопку с рюкзаком; <sbr />удерживайте палец на оружии, чтобы прочитать его описание.'),
    EMPTY,
    P('Дополнительные кнопки:'),
    K('Запал и мячик', ' - таймер 1-5 с и сила отскока (у гранат с таймером);'),
    K('Цель', ' - авиаудар, телепорт, балки: коснитесь карты, затем <sbr />нажмите кнопку огня; <b>✕</b> - отмена.'),
    P('Проведите пальцем по полю боя, чтобы сдвинуть камеру.'),
    EMPTY,
    EMPTY,
  ].join(''),
  uk: [
    K('◄\u00A0►', " - іти черв'яком уліво та вправо;"),
    K('▲\u00A0▼', ' - зміна прицілу (у зброї, яка цілиться);'),
    K('Стрибок і сальто', ' - стрибок уперед і сальто назад;'),
    K('Кнопка вогню', ' - стріляти! Утримуйте її, щоб набрати силу <sbr />пострілу, і відпустіть, щоб вистрілити.'),
    EMPTY,
    P('Якщо сила пострілу не регулюється, досить одного <sbr />натискання. Щоб вибрати зброю, натисніть кнопку з рюкзаком; <sbr />утримуйте палець на зброї, щоб прочитати її опис.'),
    EMPTY,
    P('Додаткові кнопки:'),
    K("Запал і м'ячик", ' - таймер 1-5 с і сила відскоку (у гранат з таймером);'),
    K('Ціль', ' - авіаудар, телепорт, балки: торкніться карти, потім <sbr />натисніть кнопку вогню; <b>✕</b> - скасування.'),
    P('Проведіть пальцем по полю бою, щоб зсунути камеру.'),
    EMPTY,
    EMPTY,
  ].join(''),
  en: [
    K('◄\u00A0►', ' - walk the worm left and right;'),
    K('▲\u00A0▼', ' - change the aim (for weapons that aim);'),
    K('Jump and backflip', ' - jump forward and backflip;'),
    K('Fire button', ' - fire! Hold it to build up the shot power <sbr />and release it to shoot.'),
    EMPTY,
    P('If the weapon has no power control, a single press is <sbr />enough. To pick a weapon, press the backpack button; hold a <sbr />finger on a weapon to read its description.'),
    EMPTY,
    P('Extra buttons:'),
    K('Fuse and ball', ' - timer of 1-5 s and bounce strength (timed grenades);'),
    K('Target', ' - air strike, teleport, girders: tap the map, then <sbr />press the fire button; <b>✕</b> cancels.'),
    P('Drag the battlefield with a finger to move the camera.'),
    EMPTY,
    EMPTY,
  ].join(''),
} as const;

/** Arsenal, the hotkeys (Z, X, C...) box: they need a keyboard; how to bind one stays (a tap does what the click did). */
const HOTKEYS_TOUCH = {
  ru: P('Быстрые клавиши работают <sbr />с подключенной клавиатурой. <sbr />Назначить оружие: плюсик рядом <sbr />с кнопкой, затем оружие. Отмена - <sbr />кнопка с мусорным ведром.', 12) + EMPTY,
  uk: P('Гарячі клавіші працюють <sbr />з підключеною клавіатурою. <sbr />Призначити зброю: плюсик поряд <sbr />з кнопкою, потім зброя. Скасувати - <sbr />кнопка зі смітником.', 12) + EMPTY,
  en: P('Hotkeys work with a connected <sbr />keyboard. To bind a weapon, tap <sbr />the plus next to the button, then <sbr />the weapon. To undo it - the <sbr />button with the rubbish bin.', 12) + EMPTY,
} as const;

const HOTKEYS_DESKTOP = {
  ru: /<p align="left"><font [^>]*>Для назначения оружия на быструю <sbr \/>клавишу[\s\S]*$/,
  uk: /<p align="left"><font [^>]*>Щоб призначити зброю на гарячу <sbr \/>клавішу[\s\S]*$/,
  en: /<p align="left"><font [^>]*>To bind a weapon to a hotkey, <sbr \/>[\s\S]*$/,
} as const;

/** The rules of each language, applied in order (a longer wording before a shorter one it contains). */
export const TOUCH_RULES: Record<Language, readonly TouchRule[]> = {
  ru: [
    // Tutorial (menu_view_tutorial): the controls page, the levels page, the gold page; the hotseat screen.
    { from: ', им можно управлять:</font>', to: ', им можно <sbr />управлять кнопками на экране:</font>' },
    { from: TUTORIAL_KEYS.ru, to: TUTORIAL_TOUCH.ru },
    { from: 'наведите мышь на <sbr />нижнюю шкалу.', to: 'удерживайте палец <sbr />на нижней шкале.' },
    { from: 'за одним <sbr />компьютером', to: 'на одном <sbr />устройстве' },
    // Help texts of the menus (fatigue, Wormpedia, the arsenal's hotkeys, development).
    { from: 'наведите на него курсор и нажмите', to: 'коснитесь его и нажмите' },
    { from: 'кликните на иконку с <sbr />Папой', to: 'коснитесь иконки с <sbr />Папой' },
    { from: 'при клике на шкалу <sbr />рейтинга', to: 'при нажатии на шкалу <sbr />рейтинга' },
    { from: HOTKEYS_DESKTOP.ru, to: HOTKEYS_TOUCH.ru },
    { from: 'достаточно <sbr />навести мышку на его значение.', to: 'достаточно <sbr />удерживать палец на его значении.' },
    // Weapon descriptions.
    {
      from: 'Для прицеливания используйте стрелки Вверх и Вниз; для выстрела зажимайте Пробел. Чем дольше вы его держите,',
      to: 'Для прицеливания используйте кнопки ▲\u00A0▼; для выстрела удерживайте кнопку огня. Чем дольше вы ее держите,',
    },
    {
      from: 'однако его можно менять кнопками 1, 2, 3, 4 и 5. Также кнопки + и - меняют силу отскока',
      to: 'однако его можно менять кнопкой запала (от 1 до 5 секунд). Также кнопка с мячиком меняет силу отскока',
    },
    { from: 'при максимально долгом нажатии Пробела', to: 'при максимально долгом удержании кнопки огня' },
    { from: 'После прицеливания и нажатия Пробела', to: 'После прицеливания и нажатия кнопки огня' },
    { from: 'держать Пробел при выстреле', to: 'держать кнопку огня при выстреле' },
    {
      from: 'укажите цель (двойной клик по экрану). Стрелками также можно менять',
      to: 'укажите цель (коснитесь карты и нажмите кнопку огня). Боковыми кнопками также можно менять',
    },
    { from: '(можно пробелом)', to: '(можно кнопкой огня)' },
    { from: 'нажав Пробел в нужное вам время', to: 'нажав кнопку огня в нужное вам время' },
    { from: '(стрелки Вверх/Вниз)', to: '(кнопки ▲\u00A0▼)' },
    // The scanner: its icon moved next to the timer (a small phone: above the d-pad), the game's backpack icon is hidden.
    { from: 'нажмите на иконку сканера рядом с иконкой рюкзака', to: 'нажмите на иконку сканера рядом с таймером (на маленьком экране - слева над кнопками ходьбы)' },
    { from: 'Для активации используйте Пробел, затем', to: 'Для активации нажмите кнопку огня, затем' },
    { from: 'ля активации нажмите Пробел.', to: 'ля активации нажмите кнопку огня.' },
    { from: '(стрелки Вверх и Вниз). Нажатие Пробела во время', to: '(кнопки ▲\u00A0▼). Нажатие кнопки огня во время' },
    { from: 'нажатием Пробела.', to: 'нажатием кнопки огня.' },
    { from: 'НЕ нажимайте Пробел для активации', to: 'НЕ нажимайте кнопку огня для активации' },
    { from: 'управлять полетом стрелками Вверх/Вниз/Влево/Вправо.', to: 'управлять полетом кнопками ▲\u00A0▼\u00A0◄\u00A0►.' },
    {
      from: 'После выбора Балки вы можете менять ее расположение мышью (главное, чтобы она не соприкасалсь ни с ландшафтом, ни с червяками), а также менять ее направление и длину (стрелками). Для установки Балки кликните по выбранному месту. При ошибочном выборе этого оружия жмите Escape.',
      to: 'После выбора Балки коснитесь карты там, где ее нужно поставить (главное, чтобы она не соприкасалась ни с ландшафтом, ни с червяками); направление и длину балки меняют боковые кнопки. Для установки Балки нажмите кнопку огня. При ошибочном выборе этого оружия нажмите ✕.',
    },
    { from: 'нажмите Escape, а также', to: 'нажмите ✕, а также' },
    {
      from: 'выберите его и нажмите Пробел. Для управления используйте стрелки Вверх, Влево и Вправо. Для того, чтобы снять ранец, нажмите Пробел повторно.',
      to: 'выберите его и нажмите кнопку огня. Для управления используйте кнопки ▲\u00A0◄\u00A0►. Для того, чтобы снять ранец, нажмите кнопку огня повторно.',
    },
    {
      from: 'выберите место, в которое вы хотели бы перенестись, и два раза кликните по экрану.',
      to: 'коснитесь места на карте, в которое вы хотели бы перенестись, и нажмите кнопку огня.',
    },
    {
      from: 'После активации используйте Enter для переключения между червями, для окончательного выбора нажмите Escape.',
      to: 'После активации переключайтесь между червями правой боковой кнопкой, для окончательного выбора нажмите кнопку огня.',
    },
    // Tasks.
    {
      from: 'Оружие можно выбрать кнопкой Q или нажатием на рюкзак в нижней правой части экрана. При наведении на оружие выведется его описание.',
      to: 'Оружие можно выбрать кнопкой с рюкзаком. Если удерживать палец на оружии, выведется его описание.',
    },
    { from: 'наведя курсор мыши на зеленую шкалу опыта', to: 'удерживая палец на зеленой шкале опыта' },
    {
      from: 'При наведении курсора на оружие в арсенале (или при клике на оружие в магазине) в описании',
      to: 'Если удерживать палец на оружии в арсенале (или нажать на оружие в магазине), в описании',
    },
    { from: 'наведите курсор на любой стимулятор, и нажмите', to: 'коснитесь любого стимулятора и нажмите' },
    { from: 'при клике на оружие.', to: 'при нажатии на оружие.' },
    { from: 'наведите курсор на любое оружие. Рядом', to: 'коснитесь любого оружия. Рядом' },
    // Notes of the menus and the main screen's bars.
    { from: 'при клике на иконку способности', to: 'при нажатии на иконку способности' },
    { from: 'Для подробностей кликните по иконке любого достижения.', to: 'Для подробностей коснитесь иконки любого достижения.' },
    { from: 'При клике на каждое задание', to: 'При нажатии на каждое задание' },
    { from: 'или кликните по этой шкале.', to: 'или нажмите на эту шкалу.' },
    // The connection's retry (loading status line, the battle's network overlay).
    { from: 'Щёлкните или нажмите любую клавишу, чтобы повторить сейчас.', to: 'Коснитесь экрана, чтобы повторить сейчас.' },
    { from: 'Щёлкните, чтобы повторить сейчас', to: 'Коснитесь, чтобы повторить сейчас' },
  ],
  uk: [
    { from: ', ним можна керувати:</font>', to: ', ним можна <sbr />керувати кнопками на екрані:</font>' },
    { from: TUTORIAL_KEYS.uk, to: TUTORIAL_TOUCH.uk },
    { from: 'наведіть мишу на <sbr />нижню шкалу.', to: 'утримуйте палець <sbr />на нижній шкалі.' },
    { from: "за одним <sbr />комп'ютером", to: 'на одному <sbr />пристрої' },
    { from: 'наведіть на нього курсор і натисніть', to: 'торкніться його й натисніть' },
    { from: 'клацніть на іконку з <sbr />Папою', to: 'торкніться іконки з <sbr />Папою' },
    { from: 'при клацанні на шкалу <sbr />рейтингу', to: 'після натискання на шкалу <sbr />рейтингу' },
    { from: HOTKEYS_DESKTOP.uk, to: HOTKEYS_TOUCH.uk },
    { from: 'досить <sbr />навести мишку на її значення.', to: 'досить <sbr />утримувати палець на її значенні.' },
    {
      from: 'Для прицілювання використовуйте стрілки Вгору і Вниз; для пострілу затискайте Пробіл. Що довше ви його тримаєте,',
      to: 'Для прицілювання використовуйте кнопки ▲\u00A0▼; для пострілу утримуйте кнопку вогню. Що довше ви її тримаєте,',
    },
    {
      from: 'але його можна змінювати клавішами 1, 2, 3, 4 і 5. А клавіші + та - змінюють силу відскоку',
      to: "але його можна змінювати кнопкою запалу (від 1 до 5 секунд). А кнопка з м'ячиком змінює силу відскоку",
    },
    { from: 'при найдовшому натисканні Пробілу', to: 'при найдовшому утриманні кнопки вогню' },
    { from: 'Після прицілювання й натискання Пробілу', to: 'Після прицілювання й натискання кнопки вогню' },
    { from: 'тримати Пробіл під час пострілу', to: 'тримати кнопку вогню під час пострілу' },
    {
      from: 'укажіть ціль (подвійний клік по екрану). Стрілками також можна змінювати',
      to: 'укажіть ціль (торкніться карти й натисніть кнопку вогню). Бічними кнопками також можна змінювати',
    },
    { from: '(можна пробілом)', to: '(можна кнопкою вогню)' },
    { from: 'натиснувши Пробіл у потрібний вам момент', to: 'натиснувши кнопку вогню в потрібний вам момент' },
    { from: '(стрілки Вгору/Вниз)', to: '(кнопки ▲\u00A0▼)' },
    // The scanner: its icon moved next to the timer (a small phone: above the d-pad), the game's backpack icon is hidden.
    { from: 'натисніть іконку сканера поруч з іконкою рюкзака', to: 'натисніть іконку сканера біля таймера (на малому екрані - ліворуч над кнопками ходьби)' },
    { from: 'Для активації натисніть Пробіл, потім', to: 'Для активації натисніть кнопку вогню, потім' },
    { from: 'ля активації натисніть Пробіл.', to: 'ля активації натисніть кнопку вогню.' },
    { from: '(стрілки Вгору та Вниз). Натискання Пробілу під час', to: '(кнопки ▲\u00A0▼). Натискання кнопки вогню під час' },
    { from: 'натисканням Пробілу.', to: 'натисканням кнопки вогню.' },
    { from: 'НЕ натискайте Пробіл для активації', to: 'НЕ натискайте кнопку вогню для активації' },
    { from: 'керувати стрілками Вгору/Вниз/Вліво/Вправо.', to: 'керувати кнопками ▲\u00A0▼\u00A0◄\u00A0►.' },
    {
      from: "Обравши Балку, ви можете рухати її мишею (головне, щоб вона не торкалася ні ландшафту, ні черв'яків), а також змінювати її напрямок і довжину (стрілками). Щоб установити Балку, клацніть по вибраному місцю. Якщо ви обрали цю зброю помилково, натисніть Escape.",
      to: "Обравши Балку, торкніться карти там, де її треба поставити (головне, щоб вона не торкалася ні ландшафту, ні черв'яків); напрямок і довжину балки змінюють бічні кнопки. Щоб установити Балку, натисніть кнопку вогню. Якщо ви обрали цю зброю помилково, натисніть ✕.",
    },
    { from: 'помилково, натисніть Escape, а також', to: 'помилково, натисніть ✕, а також' },
    {
      from: 'виберіть його й натисніть Пробіл. Керуйте стрілками Вгору, Вліво та Вправо. Щоб зняти ранець, натисніть Пробіл ще раз.',
      to: 'виберіть його й натисніть кнопку вогню. Керуйте кнопками ▲\u00A0◄\u00A0►. Щоб зняти ранець, натисніть кнопку вогню ще раз.',
    },
    {
      from: 'виберіть місце, куди хочете перенестися, і двічі клацніть по екрану.',
      to: 'торкніться місця на карті, куди хочете перенестися, і натисніть кнопку вогню.',
    },
    {
      from: "Після активації перемикайтеся між черв'яками клавішею Enter, а щоб остаточно підтвердити вибір, натисніть Escape.",
      to: "Після активації перемикайтеся між черв'яками правою бічною кнопкою, а щоб остаточно підтвердити вибір, натисніть кнопку вогню.",
    },
    {
      from: "Зброю можна вибрати клавішею Q або натисканням на рюкзак у правому нижньому куті екрана. Якщо навести курсор на зброю, з'явиться її опис.",
      to: "Зброю можна вибрати кнопкою з рюкзаком. Якщо утримувати палець на зброї, з'явиться її опис.",
    },
    { from: 'навівши курсор миші на зелену шкалу досвіду', to: 'утримуючи палець на зеленій шкалі досвіду' },
    {
      from: 'Коли навести курсор на зброю в арсеналі (або клацнути зброю в магазині),',
      to: 'Якщо утримувати палець на зброї в арсеналі (або натиснути зброю в магазині),',
    },
    { from: 'наведіть курсор на будь-який стимулятор і натисніть', to: 'торкніться будь-якого стимулятора й натисніть' },
    { from: 'коли клацнути на зброю.', to: 'коли натиснути на зброю.' },
    { from: 'наведіть курсор на будь-яку зброю. Поруч', to: 'торкніться будь-якої зброї. Поруч' },
    { from: 'після кліку на іконку здібності', to: 'після натискання на іконку здібності' },
    { from: 'Для подробиць клікніть на іконку будь-якого досягнення.', to: 'Для подробиць торкніться іконки будь-якого досягнення.' },
    { from: 'Після кліку на кожне завдання', to: 'Після натискання на кожне завдання' },
    { from: 'або клікніть на цю шкалу.', to: 'або натисніть на цю шкалу.' },
    { from: 'Клацніть або натисніть будь-яку клавішу, щоб повторити зараз.', to: 'Торкніться екрана, щоб повторити зараз.' },
    { from: 'Клацніть, щоб повторити зараз', to: 'Торкніться, щоб повторити зараз' },
  ],
  en: [
    { from: ', you can control it:</font>', to: ', you can <sbr />control it with the on-screen buttons:</font>' },
    { from: TUTORIAL_KEYS.en, to: TUTORIAL_TOUCH.en },
    { from: 'hover the mouse over <sbr />the bottom bar.', to: 'hold a finger on <sbr />the bottom bar.' },
    { from: 'on one <sbr />computer', to: 'on one <sbr />device' },
    { from: 'hover the cursor over it and press', to: 'tap it and press' },
    { from: 'click the icon with <sbr />Papa on it', to: 'tap the icon with <sbr />Papa on it' },
    { from: 'when you click the <sbr />rating bar', to: 'when you tap the <sbr />rating bar' },
    { from: HOTKEYS_DESKTOP.en, to: HOTKEYS_TOUCH.en },
    { from: 'it is enough to hover the mouse over its value.', to: 'it is enough to hold a finger on its value.' },
    { from: 'click it and then click <sbr />the', to: 'tap it and then tap <sbr />the' },
    { from: 'Aim with the Up and Down arrows; hold Space to fire.', to: 'Aim with the ▲\u00A0▼ buttons; hold the fire button to shoot.' },
    {
      from: 'but keys 1, 2, 3, 4 and 5 change it. The + and - keys change how hard',
      to: 'but the fuse button changes it (1 to 5 seconds). The ball button changes how hard',
    },
    { from: 'fired with Space held down for as long as possible', to: 'fired with the fire button held down for as long as possible' },
    { from: 'Once you aim and press Space', to: 'Once you aim and press the fire button' },
    { from: 'against holding Space when firing', to: 'against holding the fire button when firing' },
    {
      from: 'mark the target (double-click on the screen). The arrow keys change the direction',
      to: 'mark the target (tap the map, then press the fire button). The side buttons change the direction',
    },
    { from: '(you can trigger it with Space)', to: '(you can trigger it with the fire button)' },
    { from: 'by pressing Space at the moment you need', to: 'by pressing the fire button at the moment you need' },
    { from: '(Up/Down arrows)', to: '(the ▲\u00A0▼ buttons)' },
    // The scanner (weapon descriptions): its icon moved next to the timer, and the game's backpack icon is hidden.
    { from: 'To use it, click the scanner icon next to the backpack icon', to: 'To use it, tap the scanner icon next to the timer (on a small screen, at the left above the walking buttons)' },
    { from: 'To use it, click the scanner icon', to: 'To use it, tap the scanner icon' },
    { from: 'Press Space to activate it', to: 'Press the fire button to activate it' },
    { from: 'press Space to activate.', to: 'press the fire button to activate.' },
    { from: '(Up and Down arrows). Pressing Space during use', to: '(the ▲\u00A0▼ buttons). Pressing the fire button during use' },
    { from: 'with Space as well.', to: 'with the fire button as well.' },
    { from: 'Do NOT press Space to activate it', to: 'Do NOT press the fire button to activate it' },
    { from: 'with the Up/Down/Left/Right arrows.', to: 'with the ▲\u00A0▼\u00A0◄\u00A0► buttons.' },
    {
      from: 'After choosing Girder you can move it with the mouse (just make sure it touches neither the terrain nor any worm) and change its angle and length with the arrow keys. Click the chosen spot to set it down. Press Escape if you picked this weapon by mistake.',
      to: 'After choosing Girder, tap the map where it should go (just make sure it touches neither the terrain nor any worm); the side buttons change its angle and length. Press the fire button to set it down. Press ✕ if you picked this weapon by mistake.',
    },
    { from: 'Press Escape if you picked this weapon by mistake, and also', to: 'Press ✕ if you picked this weapon by mistake, and also' },
    {
      from: 'select it and press Space. Steer with the Up, Left and Right arrows. Press Space again to take the pack off.',
      to: 'select it and press the fire button. Steer with the ▲\u00A0◄\u00A0► buttons. Press the fire button again to take the pack off.',
    },
    { from: 'pick the spot you want to appear at and double-click the screen.', to: 'tap the spot on the map you want to appear at and press the fire button.' },
    {
      from: 'press Enter to switch between worms and Escape to confirm your choice.',
      to: 'switch between worms with the right side button and confirm your choice with the fire button.',
    },
    {
      from: 'Choose a weapon with the Q key or by clicking the backpack in the bottom right corner of the screen. Hover over a weapon to see its description.',
      to: 'Choose a weapon with the backpack button. Hold a finger on a weapon to see its description.',
    },
    { from: 'Hover the mouse over the green experience bar', to: 'Hold a finger on the green experience bar' },
    { from: 'Hover over a weapon in the arsenal (or click one in the shop)', to: 'Hold a finger on a weapon in the arsenal (or tap one in the shop)' },
    { from: 'hover over any stimulant and press', to: 'tap any stimulant and press' },
    { from: 'when you click a weapon.', to: 'when you tap a weapon.' },
    { from: 'hover the cursor over any weapon.', to: 'tap any weapon.' },
    { from: 'To buy one, click Shop in the main menu', to: 'To buy one, tap Shop in the main menu' },
    { from: 'click a perk icon and its description', to: 'tap a perk icon and its description' },
    { from: 'Click a task to see its description', to: 'Tap a task to see its description' },
    { from: 'Click any achievement icon for the details.', to: 'Tap any achievement icon for the details.' },
    { from: '(click this bar to jump there)', to: '(tap this bar to jump there)' },
    { from: 'or click this bar.', to: 'or tap this bar.' },
    { from: 'Click or press any key to retry now.', to: 'Tap the screen to retry now.' },
    { from: 'Click to retry now', to: 'Tap to retry now' },
  ],
};

/** `text` (as `lang` shows it) with the touch wording of that language; unchanged when no rule applies. */
export function touchVariant(lang: Language, text: string): string {
  let out = text;
  for (const r of TOUCH_RULES[lang]) {
    if (typeof r.from === 'string') {
      if (out.includes(r.from)) out = out.split(r.from).join(r.to);
    } else if (r.from.test(out)) out = out.replace(r.from, r.to);
  }
  return out;
}
